<template>
  <section class="main-hero">
    <header class="main-hero__head">
      <h1 class="main-hero__title text-sora text-bold mobile-font--h3">
        {{ $t("main.m_h1_compare_tokenized") }}
      </h1>
    </header>

    <article
      ref="filtersRoot"
      class="main-hero__card"
    >
      <div class="main-hero__filters">
        <div
          class="main-hero__cell"
          :class="{
            'main-hero__cell--active': popovers.location,
            'main-hero__cell--dimmed': anyPopoverOpen && !popovers.location,
          }"
          @click="onLocationCellClick"
        >
          <label class="main-hero__cell-label" for="hero-location-input">
            {{ $t("main.m_label_location") }}
          </label>
          <div class="main-hero__input-wrap">
            <input
              id="hero-location-input"
              ref="locationSearchInput"
              v-model="countrySearch"
              type="text"
              :placeholder="locationLabel"
              class="main-hero__input main-hero__input--full"
              @focus="popovers.location = true; popovers.entry = false; popovers.yield = false"
              @click.stop
            >
            <button
              v-if="filters.countries.length"
              type="button"
              class="main-hero__input-clear"
              :aria-label="$t('main.m_clear')"
              @click.stop="filters.countries = []"
            >
              ×
            </button>
          </div>
          <div
            v-if="popovers.location"
            class="hero-popover__panel hero-popover__panel--location"
            @click.stop
          >
            <ul class="hero-popover__list">
              <li v-for="opt in filteredCountryList" :key="opt.code">
                <label class="hero-popover__row">
                  <input
                    type="checkbox"
                    class="hero-popover__checkbox"
                    :checked="filters.countries.includes(opt.code)"
                    @change="toggleCountry(opt.code)"
                  >
                  <span class="hero-popover__row-name">{{ opt.label }}</span>
                  <span class="hero-popover__row-count">{{ opt.count }}</span>
                </label>
              </li>
              <li v-if="!filteredCountryList.length" class="hero-popover__empty">
                {{ $t("main.m_no_results") }}
              </li>
            </ul>
          </div>
        </div>

        <div
          class="main-hero__cell"
          :class="{
            'main-hero__cell--active': popovers.entry,
            'main-hero__cell--dimmed': anyPopoverOpen && !popovers.entry,
          }"
          @click="onCellClick('entry')"
        >
          <label class="main-hero__cell-label">
            {{ $t("main.m_label_entry") }}
          </label>
          <div class="main-hero__input-wrap">
            <span class="main-hero__input main-hero__input--readonly">
              {{ entryDisplayLabel }}
            </span>
          </div>
          <div
            v-if="popovers.entry"
            class="hero-popover__panel hero-popover__panel--slider"
            @click.stop
          >
            <base-range-slider
              v-model="entryRange"
              :min="entryBounds[0]"
              :max="entryBounds[1]"
              :step="0.1"
              :suffix="BASE_CURRENCY.symbol"
              :histogram="entryBars"
              compact
            />
          </div>
        </div>

        <div
          class="main-hero__cell"
          :class="{
            'main-hero__cell--active': popovers.yield,
            'main-hero__cell--dimmed': anyPopoverOpen && !popovers.yield,
          }"
          @click="onCellClick('yield')"
        >
          <label class="main-hero__cell-label">
            {{ $t("main.m_label_yield") }}
          </label>
          <div class="main-hero__input-wrap">
            <span class="main-hero__input main-hero__input--readonly">
              {{ yieldDisplayLabel }}
            </span>
          </div>
          <div
            v-if="popovers.yield"
            class="hero-popover__panel hero-popover__panel--slider"
            @click.stop
          >
            <base-range-slider
              v-model="yieldRange"
              :min="yieldBounds[0]"
              :max="yieldBounds[1]"
              :step="0.1"
              suffix="%"
              :histogram="yieldBars"
              compact
            />
          </div>
        </div>

        <base-button
          class="main-hero__search-btn"
          :text="$t('main.m_search_button')"
          icon-name="base/MagnifyingGlass"
          icon-size="20px"
          variant="secondary"
          size="pill-lg"
          bold-text
          no-shadow
          @click="onSearch"
        />
      </div>
    </article>

    <ul class="main-hero__chips">
      <li v-for="chip in CHIPS" :key="chip.code">
        <button
          type="button"
          class="main-hero__chip text-body-2"
          @click="onChipClick(chip)"
        >
          {{ $t(chip.label) }}
        </button>
      </li>
    </ul>

  </section>
</template>

<script setup lang="ts">
import { filtersStore } from "~/store/search_filters";
import { countryNames } from "~/utilities/constants/countryCodes";
import { BASE_CURRENCY } from "~/composables/CURRENCIES";

interface ChipDef {
  code: string;
  label: string;
  query?: Record<string, string>;
}

const CHIPS: ChipDef[] = [
  {
    code: "under_token",
    label: "main.m_chip_under_token",
    query: { "tokenPrice[gte]": "0", "tokenPrice[lte]": "50" },
  },
  {
    code: "yield_8",
    label: "main.m_chip_yield_8",
    query: { "apr[gte]": "8", "apr[lte]": "100" },
  },
  {
    code: "high_yield",
    label: "main.m_chip_high_yield",
    query: { "apr[gte]": "10", "apr[lte]": "100" },
  },
];

const useFiltersStore = filtersStore();
const localePath = useLocalePath();
const { t } = useI18n();

const filters = reactive({
  countries: [] as string[],
});

const popovers = reactive({
  location: false,
  entry: false,
  yield: false,
});

const countrySearch = ref("");
const filtersRoot = ref<HTMLElement | null>(null);
const locationSearchInput = ref<HTMLInputElement | null>(null);

const entryBounds = computed<[number, number]>(() => {
  const rate = BASE_CURRENCY.value.rate ?? 1;
  const lo = Number(useFiltersStore.currentStats?.token?.priceMin ?? 0);
  const hi = Number(useFiltersStore.currentStats?.token?.priceMax ?? 1000);
  return [
    parseFloat((lo * rate).toFixed(1)),
    parseFloat((hi * rate).toFixed(1)),
  ];
});

const yieldBounds = computed<[number, number]>(() => {
  const lo = Number(useFiltersStore.currentStats?.aprMin ?? 0);
  const hi = Number(useFiltersStore.currentStats?.aprMax ?? 30);
  return [parseFloat(lo.toFixed(1)), parseFloat(hi.toFixed(1))];
});

const entryRange = ref<[number, number]>([0, 1]);
const yieldRange = ref<[number, number]>([0, 1]);
const entryDirty = ref(false);
const yieldDirty = ref(false);

watch(
  entryBounds,
  ([lo, hi]) => {
    if (!entryDirty.value) {
      entryRange.value = [lo, hi];
    }
  },
  { immediate: true }
);

watch(
  yieldBounds,
  ([lo, hi]) => {
    if (!yieldDirty.value) {
      yieldRange.value = [lo, hi];
    }
  },
  { immediate: true }
);

watch(entryRange, ([a, b]) => {
  const [lo, hi] = entryBounds.value;
  entryDirty.value = !(a === lo && b === hi);
});

watch(yieldRange, ([a, b]) => {
  const [lo, hi] = yieldBounds.value;
  yieldDirty.value = !(a === lo && b === hi);
});

function bucketize(
  values: Record<string, number> | undefined,
  transform: (key: number) => number = (k) => k
): number[] {
  if (!values) return [];
  const pairs = Object.entries(values)
    .map(([k, count]) => ({
      key: transform(parseFloat(k)),
      count: Number(count) || 0,
    }))
    .filter((p) => Number.isFinite(p.key))
    .sort((a, b) => a.key - b.key);
  if (!pairs.length) return [];
  const lo = pairs[0].key;
  const hi = pairs[pairs.length - 1].key;
  const span = hi - lo || 1;
  const step = span / 10;
  const buckets = Array.from({ length: 10 }, () => 0);
  pairs.forEach((p) => {
    const idx = Math.min(9, Math.max(0, Math.floor((p.key - lo) / step)));
    buckets[idx] += p.count;
  });
  return buckets;
}

const entryBars = computed<number[]>(() => {
  const rate = BASE_CURRENCY.value.rate ?? 1;
  return bucketize(
    useFiltersStore.currentStats?.token?.prices as
      | Record<string, number>
      | undefined,
    (k) => k * rate
  );
});

const yieldBars = computed<number[]>(() =>
  bucketize(
    useFiltersStore.currentStats?.aprValues as
      | Record<string, number>
      | undefined
  )
);

const formatMoney = (n: number): string => {
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  if (Number.isInteger(n)) return n.toString();
  return n.toFixed(1);
};

const entryDisplayLabel = computed(() => {
  if (!entryDirty.value) return t("main.m_any");
  const sym = BASE_CURRENCY.value.symbol;
  return `${sym}${formatMoney(entryRange.value[0])} – ${sym}${formatMoney(entryRange.value[1])}`;
});

const yieldDisplayLabel = computed(() => {
  if (!yieldDirty.value) return t("main.m_any");
  return `${yieldRange.value[0].toFixed(1)}% – ${yieldRange.value[1].toFixed(1)}%`;
});

const onLocationCellClick = async () => {
  const wasOpen = popovers.location;
  popovers.entry = false;
  popovers.yield = false;
  popovers.location = !wasOpen;
  if (popovers.location) {
    await nextTick();
    locationSearchInput.value?.focus();
  } else {
    locationSearchInput.value?.blur();
  }
};

const onCellClick = (which: "entry" | "yield") => {
  const wasOpen = popovers[which];
  popovers.location = false;
  popovers[which === "entry" ? "yield" : "entry"] = false;
  popovers[which] = !wasOpen;
};

const anyPopoverOpen = computed(
  () => popovers.location || popovers.entry || popovers.yield
);

const closeAllPopovers = () => {
  popovers.location = false;
  popovers.entry = false;
  popovers.yield = false;
};

const onDocumentClick = (e: MouseEvent) => {
  const root = filtersRoot.value;
  if (!root) return;
  if (!root.contains(e.target as Node)) {
    closeAllPopovers();
  }
};

onMounted(() => document.addEventListener("click", onDocumentClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocumentClick));

const countryList = computed(() => {
  const stats = useFiltersStore.currentStats?.country || {};
  return Object.keys(stats)
    .filter((code) => code !== "null")
    .map((code) => ({
      code,
      label: countryNames[code] || code,
      count: Number(stats[code] ?? 0),
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
});

const filteredCountryList = computed(() => {
  const q = countrySearch.value.trim().toLowerCase();
  if (!q) return countryList.value;
  return countryList.value.filter((c) => c.label.toLowerCase().includes(q));
});

const toggleCountry = (code: string) => {
  const idx = filters.countries.indexOf(code);
  if (idx >= 0) filters.countries.splice(idx, 1);
  else filters.countries.push(code);
};

const locationLabel = computed(() => {
  const n = filters.countries.length;
  if (n === 0) return t("main.m_anywhere");
  if (n === 1) return countryNames[filters.countries[0]] || filters.countries[0];
  return t("main.m_countries_selected", { count: n });
});

const buildQuery = (): Record<string, string> => {
  const q: Record<string, string> = {};
  filters.countries.forEach((code, i) => {
    q[`countryId[${i}]`] = code;
  });

  if (entryDirty.value) {
    const usdRate = BASE_CURRENCY.value.usdRate ?? 1;
    q["tokenPrice[gte]"] = (entryRange.value[0] * usdRate).toFixed(1);
    q["tokenPrice[lte]"] = (entryRange.value[1] * usdRate).toFixed(1);
  }

  if (yieldDirty.value) {
    q["apr[gte]"] = yieldRange.value[0].toFixed(1);
    q["apr[lte]"] = yieldRange.value[1].toFixed(1);
  }

  return q;
};

const onSearch = () => {
  navigateTo(localePath({ path: "/directory", query: buildQuery() }));
};

const onChipClick = (chip: ChipDef) => {
  navigateTo(localePath({ path: "/directory", query: chip.query ?? {} }));
};

</script>

<style scoped lang="scss">
.main-hero {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 32px;
  background: var(--header-bg);
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  margin-top: -32px;
  padding: 56px max(24px, calc(50vw - 696px)) 80px;
  box-sizing: border-box;

  &__head {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__title {
    margin: 0;
    color: var(--text-primary);
    font-family: var(--font-display);
    font-size: var(--font-display-1);
    line-height: var(--lh-display);
    letter-spacing: -1px;
    max-width: 18ch;
  }

  &__card {
    position: relative;
    background: var(--surface-muted);
    border-radius: var(--radius-pill);
    box-shadow: var(--shadow-card);
    padding: 6px 6px 6px 6px;
  }

  &__filters {
    position: relative;
    display: flex;
    align-items: stretch;
    gap: 0;
  }

  &__cell {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
    padding: 10px 24px;
    border: none;
    border-radius: 999px;
    min-width: 0;
    flex: 1 1 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
    outline: none;
    transition:
      background 0.28s cubic-bezier(0.32, 0.72, 0, 1),
      box-shadow 0.28s cubic-bezier(0.32, 0.72, 0, 1),
      opacity 0.28s cubic-bezier(0.32, 0.72, 0, 1);
    will-change: background, box-shadow, opacity;

    &:not(:first-of-type)::before {
      content: "";
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 1px;
      height: 60%;
      background: var(--border-subtle);
      transition: opacity 0.2s ease;
    }

    &:hover:not(.main-hero__cell--active) {
      background: rgba(255, 255, 255, 0.6);
    }
    &:focus-visible {
      box-shadow: 0 0 0 2px var(--black-monochrome);
    }
  }

  /* Hide separators around the active cell (Airbnb-style) */
  &__cell--active::before,
  &__cell--active + &__cell::before {
    opacity: 0;
  }

  &__cell--button {
    @include drop-button-styles;
    padding: 10px 24px;
    cursor: pointer;
    border-radius: 999px;
  }

  &__cell--dimmed {
    opacity: 0.55;
    &:hover {
      opacity: 0.85;
    }
  }

  &__cell--active {
    z-index: 21;
    opacity: 1;
    background: var(--surface);
    box-shadow: var(--shadow-card-hover);
    & > .main-hero__cell-label,
    & > .main-hero__input-wrap {
      position: relative;
      z-index: 2;
    }
  }

  &__cell-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--black-monochrome-60);
  }

  &__input-wrap {
    display: flex;
    align-items: center;
    gap: 2px;
    justify-content: flex-start;
  }

  &__input {
    flex: 1 1 0;
    min-width: 0;
    width: 100%;
    background: transparent;
    border: none;
    outline: none;
    padding: 0;
    margin: 0;
    font-family: inherit;
    font-size: 16px;
    font-weight: 600;
    color: var(--black-monochrome);
    &::placeholder {
      color: var(--black-monochrome-72);
      font-weight: 600;
    }
  }

  &__input-wrap &__input {
    flex: 0 1 auto;
    width: auto;
    field-sizing: content;
    min-width: 2ch;
    max-width: 100%;
  }

  &__input-wrap &__input--full {
    flex: 1 1 auto;
    width: 100%;
    field-sizing: auto;
    min-width: 0;
    max-width: none;
  }

  &__input-wrap &__input--readonly {
    flex: 1 1 auto;
    width: 100%;
    field-sizing: auto;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    color: var(--black-monochrome-72);
  }

  &__input-clear {
    appearance: none;
    border: none;
    background: transparent;
    color: var(--black-monochrome-60);
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
    padding: 0 4px;
    margin-left: 4px;
    flex-shrink: 0;
    transition: color 0.15s ease;
    &:hover {
      color: var(--black-monochrome);
    }
  }

  &__search-btn.base-button {
    flex: 0 0 auto;
    width: max-content;
    align-self: stretch;
    margin-left: 8px;
    height: auto;
    min-height: 0;
    padding-top: 0;
    padding-bottom: 0;
  }

  &__chips {
    list-style: none;
    margin: -16px 0 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__chip {
    @include drop-button-styles;
    padding: 8px 16px;
    border-radius: var(--radius-pill);
    border: 1px solid var(--border-subtle);
    background: var(--surface);
    color: var(--text-secondary);
    cursor: pointer;
    box-shadow: var(--shadow-card);
    transition: box-shadow 0.2s ease, color 0.2s ease;
    &:hover {
      color: var(--text-primary);
      box-shadow: var(--shadow-card-hover);
    }
  }

}

@media (max-width: 75rem) {
  @include mobile-fonts;
  .main-hero {
    margin-top: -16px;
    padding-top: 32px;
    padding-bottom: 56px;

    &__title {
      font-size: 36px;
      line-height: 1.1;
      letter-spacing: -0.5px;
      max-width: none;
    }

    &__cell,
    &__cell--button {
      padding: 10px 16px;
    }
  }

  .hero-popover__panel,
  .hero-popover__panel--slider {
    min-width: 100%;
    padding: 16px 18px;
  }
}

@media (max-width: 56rem) {
  .main-hero {
    &__cell,
    &__cell--button {
      padding: 10px 12px;
    }

    &__cell-value,
    &__input--readonly {
      font-size: 14px;
    }

    // Keep real <input> at 16px to prevent iOS auto-zoom on focus
    &__input {
      font-size: 16px;
    }

    &__search-btn.base-button {
      margin-left: 4px;
    }
  }

  .hero-popover__panel,
  .hero-popover__panel--slider {
    padding: 14px;
  }
}

@media (max-width: 48rem) {
  .main-hero {
    padding-left: 16px;
    padding-right: 16px;
  }
}

@media (max-width: 44rem) {
  .main-hero {
    gap: 24px;
    margin-top: -16px;
    padding-top: 20px;
    padding-bottom: 40px;

    &__card {
      border-radius: 24px;
      padding: 8px;
    }

    &__filters {
      flex-direction: column;
      align-items: stretch;
      gap: 0;
    }

    &__cell,
    &__cell--button {
      flex: 1 1 auto;
      width: 100%;
      padding: 12px 16px;
      border-radius: 16px;

      &:not(:first-of-type)::before {
        display: none;
      }
    }

    &__search-btn.base-button {
      flex: 1 1 auto;
      width: 100%;
      margin-left: 0;
      margin-top: 8px;
      min-height: 48px;
    }
  }

  .hero-popover__panel,
  .hero-popover__panel--slider {
    min-width: 100%;
    padding: 16px;
    border-radius: 16px;
  }
}

@media (max-width: 32rem) {
  .main-hero {
    gap: 20px;
    padding-top: 16px;
    padding-bottom: 32px;

    &__title {
      font-size: 28px;
      line-height: 1.1;
      letter-spacing: -0.3px;
    }

    &__cell,
    &__cell--button {
      padding: 10px 14px;
    }

    &__chips {
      gap: 6px;
    }

    &__chip {
      padding: 6px 12px;
      font-size: 13px;
    }
  }
}
</style>

<style lang="scss">
.hero-popover__panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: var(--white-monochrome);
  border-radius: 16px;
  box-shadow:
    0 2px 6px rgba(18, 18, 18, 0.06),
    0 12px 28px rgba(18, 18, 18, 0.10);
  padding: 20px 24px;
  min-width: max(100%, 340px);
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: inherit;
  z-index: 1;
  text-align: left;
  transform-origin: top center;
  animation: heroPopoverIn 0.28s cubic-bezier(0.32, 0.72, 0, 1) both;

  &--location {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  &--slider {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    padding: 20px 22px;
    gap: 14px;
  }
}

@keyframes heroPopoverIn {
  from {
    opacity: 0;
    transform: translateY(-6px) scale(0.985);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.hero-popover__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 280px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-content: flex-start;
  scrollbar-width: thin;
  scrollbar-color: var(--black-monochrome-40, rgba(18, 18, 18, 0.25)) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(18, 18, 18, 0.25);
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(18, 18, 18, 0.4);
  }
}

.hero-popover__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  color: var(--black-monochrome);
  &:hover {
    background: var(--white-contrast);
  }
}

.hero-popover__checkbox {
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: var(--black-monochrome);
  cursor: pointer;
  flex-shrink: 0;
}

.hero-popover__row-name {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hero-popover__row-count {
  font-size: 12px;
  color: var(--black-monochrome-60);
  flex-shrink: 0;
}

.hero-popover__empty {
  padding: 16px 10px;
  text-align: center;
  font-size: 13px;
  color: var(--black-monochrome-60);
}

</style>
